import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { groupBy } from 'lodash'
import { DateTime } from 'luxon'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import IWFormSection from 'shared/components/thunderbolt/IWFormSectionContainer'
import IWGenericCard, {
  IWGenericCardBody,
} from 'shared/components/thunderbolt/IWGenericCard'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWNoResultsMessage from 'shared/components/thunderbolt/IWNoResultsMessage'
import IWTab from 'shared/components/thunderbolt/IWTab'
import IWTabs from 'shared/components/thunderbolt/IWTabs'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import { TIMEZONES } from 'shared/constants'
import LayoutContext from 'shared/contexts/LayoutContext'
import { convertToCVS, downloadContent } from 'shared/helpers'
import { getSubmissionHistory } from 'shared/loadSchedulingClient'
import { getPodList } from 'shared/podServiceClient'
import { WizardStepProps } from 'shared/types'
import styled from 'styled-components'
import { Pod } from '../../insightsManager/types'
import { convertDemandBidsCsvToJson, getLsData } from '../helpers'
import { getDayAheadBidsConvertSchema } from '../schemas'
import { LsCsvRowItem, SubmissionHistory } from '../types'
import FileUploadSection from './FileUploadSection'
import LSPodList from './LSPodList'
import { LSPreviousSubmittedTab } from './LSPreviousSubmittedTab'

const StyledCardBody = styled(IWGenericCardBody)`
  padding-top: 1rem;
`

const CONSTANTS = {
  TOAST_DURATION: 10_000,
  SUCCESS_TOAST_DURATION: 5_000,
  PODS_QUERY_LIMIT: 100,
} as const

type StepState = {
  dayAheadDemandBids: LsCsvRowItem[]
  selectedMarket?: string
  selectedFilename?: string
}

type PreviousSubmissions = {
  market: string
  brand: string
  createdAt: string | Date
  timezone: string
  submissionGroupId: string
  data: SubmissionHistory[]
}

type StepCurrentState = {
  demandBids: LsCsvRowItem[]
  previousSubmissions: PreviousSubmissions[]
  stfPods: Pod[]
  hasErrors: boolean
  isDownloading: boolean
}

export type Props = WizardStepProps<StepState>

const toastOptions = {
  canDismiss: true,
  duration: CONSTANTS.TOAST_DURATION,
}

export default function SelectStep({ onChange }: Props) {
  const toast = useToast()

  const { t } = useTranslation()
  const { timezone } = useContext(LayoutContext)

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [currentState, setCurrentState] = useState<StepCurrentState>({
    demandBids: [],
    previousSubmissions: [],
    hasErrors: true,
    stfPods: [],
    isDownloading: false,
  })

  const { data: pods, isLoading: isLoadingPods } = useQuery(
    ['shortTermForecast-ls-submit'],
    async () => {
      const listOfPods = await getPodList(0, CONSTANTS.PODS_QUERY_LIMIT, '', [
        'shortTermForecast',
      ])

      return listOfPods.rows.filter((v) => v.useForLoadScheduling)
    },
  )

  const { data: previousSubmissions, isLoading: isPreviousSubmittedLoading } =
    useQuery({
      queryKey: ['shortTermForecast-ls-submit-history'],
      queryFn: async () => {
        const submissions = await getSubmissionHistory({
          startDate: DateTime.local().setZone(timezone).toISODate()!,
          endDate: DateTime.local()
            .setZone(timezone)
            .plus({ days: 10 })
            .toISODate()!,
        })

        return submissions.filter(
          (s) => s.market.toUpperCase() !== 'ERCOT' && s.wasSuccessful,
        )
      },
    })

  const onUpload = async (filename: string, file: File) => {
    try {
      const json = await convertDemandBidsCsvToJson(file)

      setCurrentState({
        ...currentState,
        hasErrors: false,
        demandBids: json as LsCsvRowItem[],
      })

      toast.grey(t('loadScheduling.toasts.uploadSuccessful'), {
        icon: faCheck,
        description: filename,
        canDismiss: true,
        duration: CONSTANTS.SUCCESS_TOAST_DURATION,
      })
    } catch (error) {
      console.error(error)
      toast.alert(t('errors.generic.processing'), toastOptions)
    }
  }

  const onDownload = async ({
    datasource = '',
    userPodId,
    userPodName,
    markets,
  }: Pod) => {
    try {
      setCurrentState((prev) => ({
        ...prev,
        demandBids: [],
        isDownloading: true,
      }))

      const [currentMarket] = markets
      const marketTimezone = TIMEZONES[currentMarket.toUpperCase()]

      const demandBids = await getLsData(datasource, marketTimezone, markets)

      const csvContent = convertToCVS(
        demandBids.updatedData,
        getDayAheadBidsConvertSchema().map(({ accessor }) => accessor),
      )

      if (!csvContent) {
        toast.warning(t('loadScheduling.convertWarningMessage'), toastOptions)

        setCurrentState((prev) => ({
          ...prev,
          isDownloading: false,
        }))

        return
      }

      downloadContent({
        filename: `${userPodName}-${userPodId}`,
        content: csvContent,
        ext: 'csv',
        mediaType: 'text/csv',
      })
    } catch (err) {
      console.error(err)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        demandBids: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  const onSelect = async ({ datasource = '', markets }: Pod) => {
    try {
      setCurrentState((prev) => ({
        ...prev,
        demandBids: [],
        isDownloading: true,
      }))

      const [currentMarket] = markets
      const marketTimezone = TIMEZONES[currentMarket.toUpperCase()]

      const data = await getLsData(datasource, marketTimezone, markets)

      if (data.updatedData.length <= 0) {
        toast.warning(
          t('loadScheduling.toasts.noSubmittableDatesFound'),
          toastOptions,
        )
        return
      }

      setCurrentState((prev) => ({
        ...prev,
        demandBids: data.updatedData as LsCsvRowItem[],
        hasErrors: false,
      }))
    } catch (e) {
      console.error(e)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        demandBids: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  const onSelectPreviousSubmission = (
    submissions: SubmissionHistory[] = [],
  ) => {
    try {
      console.log(submissions)

      if (submissions.length === 0) {
        return
      }

      setCurrentState((prev) => ({
        ...prev,
        isDownloading: true,
        demandBids: [],
      }))

      const transformed = submissions.map((item) => {
        const result = {
          market: item.market,
          brand: item.brand,
          date: item.date.toString(),
          zone: item.zone,
          location: item.location,
          location_name: item.locationName,
        }

        for (const { interval, load } of item.intervals) {
          result[interval.toString()] = load
        }

        return result as LsCsvRowItem
      })

      if (transformed.length <= 0) {
        toast.warning(
          t('loadScheduling.toasts.noSubmittableDatesFound'),
          toastOptions,
        )
        return
      }

      setCurrentState((prev) => ({
        ...prev,
        demandBids: transformed,
        hasErrors: false,
      }))
    } catch (err) {
      console.error(err)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        demandBids: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  const onDownloadPreviousSubmission = (
    submissions: SubmissionHistory[] = [],
  ) => {
    try {
      if (submissions.length === 0) {
        return
      }

      const { market } = submissions[0]

      setCurrentState((prev) => ({
        ...prev,
        isDownloading: true,
        demandBids: [],
      }))

      const transformed = submissions.map((item) => {
        const obj = {
          market: item.market,
          brand: item.brand,
          zone: item.zone,
          location: item.location,
          location_name: item.locationName,
          date: item.date,
        }

        item.intervals.forEach((v) => {
          obj[v.interval] = v.load
        })

        return obj
      })

      const csvContent = convertToCVS(
        transformed,
        getDayAheadBidsConvertSchema().map(({ accessor }) => accessor),
      )

      if (!csvContent) {
        toast.warning(t('loadScheduling.convertWarningMessage'), toastOptions)

        setCurrentState((prev) => ({
          ...prev,
          isDownloading: false,
        }))

        return
      }

      downloadContent({
        filename: `ls-${market}-${DateTime.local()
          .setZone(timezone)
          .toMillis()}`,
        content: csvContent,
        ext: 'csv',
        mediaType: 'text/csv',
      })
    } catch (err) {
      console.error(err)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        demandBids: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  useEffect(() => {
    onChange(
      {
        dayAheadDemandBids: currentState.demandBids,
      },
      !currentState.hasErrors && currentState.demandBids.length > 0,
    )
  }, [currentState.demandBids, currentState.hasErrors])

  useEffect(() => {
    if (!pods?.length) {
      return
    }

    const stfPods = pods.filter(
      (p) =>
        p.useForLoadScheduling &&
        !p.markets.includes('ercot') &&
        !p.markets.includes('ERCOT'),
    )

    setCurrentState((prev) => {
      return {
        ...prev,
        stfPods,
      }
    })
  }, [pods])

  useEffect(() => {
    if (!previousSubmissions) {
      return
    }

    const groupedBySubmissionGroupId = groupBy(
      previousSubmissions.filter(
        (v) => v.schedulingType === 'DEMAND_SCHEDULING',
      ),
      ({ market, brand, submissionGroupId }) =>
        `${market}-${brand}-${submissionGroupId}`,
    )

    const mapped = Object.entries(groupedBySubmissionGroupId).map((element) => {
      const [, value] = element
      const [first] = value
      const { market, brand, createdAt, submissionGroupId } = first

      return {
        market,
        brand,
        createdAt,
        timezone,
        submissionGroupId,
        data: value,
      }
    })

    setCurrentState((prev) => {
      return {
        ...prev,
        previousSubmissions: mapped,
      }
    })
  }, [previousSubmissions])

  return (
    <>
      <IWGenericCard>
        <StyledCardBody>
          <FileUploadSection
            onChange={(filename, file) => {
              onUpload(filename, file)
            }}
            onClear={() => {
              setCurrentState((prev) => ({
                ...prev,
                demandBids: [],
              }))
            }}
          />
        </StyledCardBody>
      </IWGenericCard>

      {!isLoadingPods &&
        !isPreviousSubmittedLoading &&
        currentState.stfPods.length === 0 &&
        currentState.previousSubmissions.length === 0 && <IWNoResultsMessage />}

      {(currentState.stfPods.length > 0 ||
        currentState.previousSubmissions.length > 0) && (
        <IWGenericCard>
          <StyledCardBody>
            <IWFormSection
              sectionTitle={t('loadScheduling.titles.selectSTFForecast')}
              sectionDescription={t(
                'loadScheduling.descriptions.selectSTFForecast',
              )}
            >
              <IWTabs
                activeTabIndex={activeTabIndex}
                onTabChange={(tabIndex) => {
                  setActiveTabIndex(tabIndex)
                }}
              >
                <IWTab label={t('loadScheduling.labels.currentLsInnoPods')} />
                <IWTab
                  label={t('loadScheduling.labels.previousSubmittedLsInnoPods')}
                />
              </IWTabs>

              {activeTabIndex === 0 && (
                <>
                  {isLoadingPods && <IWLoading />}

                  <LSPodList
                    pods={currentState.stfPods}
                    isLoading={currentState.isDownloading}
                    onDownload={onDownload}
                    onSelect={onSelect}
                    selectedPodIds={[]}
                  />
                </>
              )}

              {activeTabIndex === 1 && (
                <>
                  {isPreviousSubmittedLoading && <IWLoading />}

                  <LSPreviousSubmittedTab
                    items={currentState.previousSubmissions}
                    onSelect={onSelectPreviousSubmission}
                    onDownload={onDownloadPreviousSubmission}
                  />
                </>
              )}
            </IWFormSection>
          </StyledCardBody>
        </IWGenericCard>
      )}
    </>
  )
}
